import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useLocation } from 'react-router-dom';
import DBConfirmModal from '../../common/DBConfirmModal';
import { useTheme } from '@mui/material/styles';
import { usePersistedDataStore } from '../store';
import { toast } from '../../common/toast';
import cloneDeep from 'lodash/cloneDeep';
import DataGridContainer from './DataGridContainer';
import { createCaseReviewGridStyles } from './config/caseReviewGridStyles';
import { createErrorsArray } from '../../utils/compErrorFlagging';
import TableCustomToolbar from './TableCustomToolbar';
import TownEvidenceTable from './TownEvidenceTable';
import { getTrainingUniquePids, isSelectionEqualToTraining as isSelectionEqualToTrainingUtil } from './common/trainingUtils';
import logger from '../../utils/logger';
import { compRowId, normalizeCompId, selectionKeyForRow, selectionSetHasRow } from './common/compSheetLookup';




// Instead of 
const MuiTable = React.memo(({
  headers,
  handleUpdateStateCallback,
  reviewDetails,
  viewCallback,
  rowUpdate,
  view,
  selectedComps,
  addCompNotInResponseCallback,
  filteredHeaders,
  handleSetPropertyInfo,
  handleColumnVisibilityChange,
  memoizedColumnVisibilityModel,
  columnLayout,
  isSaving,
  setIsSaving,
  unsavedChangesRef,
  handleProcessDBUpdate,
  handleCaseNotesCleanup,
  caseNotes,
  originalRows,
  rawComps,
  updateSelectedComps,
  subject,
  subjectIFMV,
  tableProps,
  savedCompPids,
  handleSaveComps,
  onFilteredRowsChange,
  mapFilteredParcelIds,
  isFiltered = false,
  onCaseInfoClick,
  caseObject,
  caseId,
  onClearMapFilter,
  onManualRefresh,
  showRefreshButton,
  isRefreshing,
  regularComps,
  hideToolbar = false,
  hideCustomToolbar = false,
  isSettled = false,
    // Negotiation-specific props
    updateNegotiationComps,
    addNegotiationComp,
    handleDeleteProperty,
    handleAddressClick,
    negotiationCompsTouched,
    markNegotiationCompsTouched,
    // Props needed for adj_price tooltips
    memoizedSelectedAdjustments,
    savedComps,
    memoizedCompObject,
  townCompParcelIds = [],
  isSaleRun = false,
}) => {
  const startingRows = useMemo(() => cloneDeep(originalRows), [originalRows]);
  const extraAddedRowsRef = useRef([]);
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const apiRef = useGridApiRef();
  const caseNotesRef = useRef(null);

  // Helper function to search for a comp in raw data by parcel_id (and optional TransactionID)
  const findCompInRawData = useCallback((parcel_id, transactionId = null) => {
    if (!rawComps || !rawComps.parcel_id || !Array.isArray(rawComps.parcel_id)) {
      return null;
    }

    let index = -1;
    if (transactionId != null && transactionId !== '' && Array.isArray(rawComps.TransactionID)) {
      const needle = String(transactionId);
      index = rawComps.TransactionID.findIndex(
        (id, i) => rawComps.parcel_id[i] === parcel_id && id != null && String(id) === needle,
      );
    }
    if (index === -1) {
      index = rawComps.parcel_id.findIndex(pid => pid === parcel_id);
    }
    if (index === -1) {
      return null;
    }

    const txnAtIndex = rawComps.TransactionID?.[index];
    // Create a row object from the raw data at this index
    const row = {
      id: compRowId(parcel_id, txnAtIndex),
      parcel_id: parcel_id,
      TransactionID: txnAtIndex ?? '',
      '#': index + 1, // Original position in raw data
      PropertyInfo: {
        Address: rawComps.Address?.[index] || '',
        parcel_id: parcel_id,
        SDName: rawComps.SDName?.[index] || '',
        SDCode: rawComps.SDCode?.[index] || ''
      },
      // Add other essential properties that might be needed
      Sqft: rawComps.Sqft?.[index] || '',
      IFMV: rawComps.IFMV?.[index] || '',
      SalePrice: rawComps.SalePrice?.[index] || '',
      // Add any other properties that are commonly used
      ...Object.fromEntries(
        Object.keys(rawComps).map(key => [key, rawComps[key]?.[index]])
      )
    };
    
    return row;
  }, [rawComps]);
  const [caseNotesChanged, setCaseNotesChanged] = useState(false);
  const getCaseNotes = usePersistedDataStore((state) => state.caseNotes);

  // With just a single state:
  const [isPinned, setIsPinned] = useState(() => {
    const storedPinValue = localStorage.getItem('pinnedComps');
    return storedPinValue !== null ? JSON.parse(storedPinValue) : view !== 'regular';
  });

  const handlePinToggle = useCallback(() => {
    setIsPinned(prev => {
      const newValue = !prev;
      localStorage.setItem('pinnedComps', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Use the memoized queryParams object to calculate 'comp'
  const comp = useMemo(() => parseInt(queryParams.get('comp')) - 1 || 0, [queryParams]);
  const [reordering, setReordering] = useState(false);
  const reOrderingRef = useRef(false);
  const [userReordered, setUserReordered] = useState(false);
  const forceToolbarUpdate = useCallback(() => {
    usePersistedDataStore.getState().bumpCaseReviewToolbarRevision();
  }, []);

  const savedCompPidsRef = useRef(savedCompPids);
  useEffect(() => {
    savedCompPidsRef.current = savedCompPids;
  }, [savedCompPids]);

  // when you change comps, clear out the datagrid internal state for selected comp / edited inventory.
  const handleApiOperations = useCallback((savedCompOverride = savedCompPidsRef.current) => {
    // return
    if (!apiRef.current) return;
    
    const columns = apiRef.current.getAllColumns();
    const checkboxColumn = columns.find(col => col.field === 'actions');
    apiRef.current.setFilterModel({ items: [] });
    apiRef.current.setSortModel([]);

    if (checkboxColumn) {
      const selection = Array.isArray(savedCompOverride) ? savedCompOverride : [];
      checkboxColumn.selectionArray = selection;
      // Match row-checkbox behavior: any non-empty selection uses indeterminate (minus), not a solid checked header.
      checkboxColumn.indeterminate = selection.length > 0;
      checkboxColumn.reOrderArray = [];
      apiRef.current.updateColumns([checkboxColumn]);
    }

    reOrderingRef.current = [];

    unsavedChangesRef.current = {
      originalSubject: {},
      unsavedRows: {},
      rowsBeforeChange: {},
      unsavedSubject: {},
    };
  }, [apiRef]);

  // Map of parcel_id -> smallest training comp index (used by checkbox rendering)
  const trainingMinIndexByParcelId = useMemo(() => {
    try {
      const result = {};
      const trainingComps = caseObject?.TrainingComps || [];
      if (!Array.isArray(trainingComps)) return result;
      trainingComps.forEach((entry) => {
        const compsDict = entry?.Comps || {};
        Object.entries(compsDict).forEach(([idx, pid]) => {
          if (!pid) return;
          const n = Number(idx);
          if (Number.isNaN(n)) return;
          if (result[pid] == null) result[pid] = n;
          else result[pid] = Math.min(result[pid], n);
        });
      });
      return result;
    } catch (e) {
      return {};
    }
  }, [caseObject]);

  // Unique list of training PIDs for equality comparison
  const trainingUniquePids = useMemo(() => getTrainingUniquePids(caseObject), [caseObject]);

  // Build map of parcel_id -> training userIds who saved it (before row class hook)
  const trainingCompUsersByParcelId = useMemo(() => {
    try {
      const result = {};
      const trainingComps = caseObject?.TrainingComps || [];
      if (!Array.isArray(trainingComps)) return result;
      trainingComps.forEach((entry) => {
        const userId = entry?.UserID;
        const compsDict = entry?.Comps || {};
        if (!userId || !compsDict) return;
        Object.values(compsDict).forEach((pid) => {
          if (!pid) return;
          if (!result[pid]) result[pid] = new Set();
          result[pid].add(userId);
        });
      });
      Object.keys(result).forEach((pid) => {
        result[pid] = Array.from(result[pid]);
      });
      return result;
    } catch (e) {
      return {};
    }
  }, [caseObject]);

  // Map each Training user to a stable ordinal (1..4 capped) to color rows per-user
  const trainingUserOrdinalMap = useMemo(() => {
    try {
      const uniqueUsers = [];
      const trainingComps = caseObject?.TrainingComps || [];
      if (Array.isArray(trainingComps)) {
        trainingComps.forEach((entry) => {
          const userId = entry?.UserID;
          if (userId && !uniqueUsers.includes(userId)) uniqueUsers.push(userId);
        });
      }
      const map = {};
      uniqueUsers.forEach((userId, idx) => {
        map[userId] = Math.min(idx + 1, 4);
      });
      return map;
    } catch (e) {
      return {};
    }
  }, [caseObject]);

  // Updated this to be based on your actual original rows instead of deep clone (for inventory update purposes)
  const { rows, pinnedRows } = useMemo(() => {
    // For town view, skip all selection-based filtering and just return the rows as-is
    if (view === 'town') {
      return {
        rows: startingRows,
        pinnedRows: { top: [], bottom: [] }
      };
    }

    const knownIds = new Set(startingRows.map((row) => row.id));
    extraAddedRowsRef.current = extraAddedRowsRef.current.filter((row) => row?.id != null && !knownIds.has(row.id));
    const rowsForProcessing = extraAddedRowsRef.current.length
      ? [...startingRows, ...extraAddedRowsRef.current]
      : startingRows;

    // Keep original pinned selection
    const pinningSource = selectedComps ?? savedCompPids ?? [];

    // Build set-equality flag (ignore order)
    const isSelectionEqualToTraining = isSelectionEqualToTrainingUtil(pinningSource, trainingUniquePids);

    // Create a map to maintain selection order
    const selectionOrderMap = new Map();
    // If reordering, use reordering ref for numbers, otherwise use original selection
    const numberSource = reordering ? (reOrderingRef.current || []) : pinningSource;
    numberSource.forEach((pid, index) => {
      selectionOrderMap.set(normalizeCompId(pid), index + 1);
    });

    // Process all rows first to maintain consistent selection numbers
    const pinningSet = new Set((pinningSource || []).map(normalizeCompId).filter(Boolean));
    const processedRows = rowsForProcessing.map(row => {
      const unsavedChanges = unsavedChangesRef.current.unsavedRows?.[row.id] || {};
      const selectionKey = selectionKeyForRow(row, isSaleRun);
      const isSelected = pinningSet.has(selectionKey);
      // During reordering, only show numbers for rows in reordering ref
      const selectedRowNumber = reordering 
        ? (reOrderingRef.current?.map(normalizeCompId).includes(selectionKey) ? selectionOrderMap.get(selectionKey) || selectionOrderMap.get(row.parcel_id) : 0)
        : (isSelected ? (selectionOrderMap.get(selectionKey) || selectionOrderMap.get(normalizeCompId(row.parcel_id)) || 0) : 0);
      const trainingSelectedNumberRaw = trainingMinIndexByParcelId[row.parcel_id] || 0;
      const trainingSelectedNumber = isSelectionEqualToTraining ? 0 : trainingSelectedNumberRaw;

      // Derive a stable training user ordinal for this row to drive consistent coloring
      // Matches the logic used in getRowClassName for per-user color selection
      let trainingUserOrdinal = 0;
      try {
        const tUsers = trainingCompUsersByParcelId[row.parcel_id];
        if (tUsers && tUsers.length > 0) {
          const ordinals = tUsers
            .map((uid) => trainingUserOrdinalMap[uid])
            .filter((n) => !!n)
            .sort((a, b) => a - b);
          trainingUserOrdinal = ordinals.length > 0 ? ordinals[0] : 0;
        }
      } catch (e) {
        trainingUserOrdinal = 0;
      }
      return {
        ...row,
        ...unsavedChanges, // Merge unsaved changes into the row
        selected: isSelected,
        selectedRowNumber: selectedRowNumber,
        trainingSelectedNumber,
        trainingUserOrdinal
      };
    });

    // If not pinned, return all processed rows
    if (!isPinned) {
      return {
        rows: processedRows,
        pinnedRows: { top: [], bottom: [] }
      };
    }

    // If pinned, separate into pinned and non-pinned rows
    const pinnedRowsData = { top: [], bottom: [] };
    const regularRows = [];

    // Add selected rows to pinned section in their original order
    const selectedRows = processedRows.filter(row => row.selected);
    
    // Maintain original pinned order by using pinningSource
    const orderedSelectedRows = [...selectedRows].sort((a, b) => {
      const aIndex = pinningSource.findIndex((id) => normalizeCompId(id) === selectionKeyForRow(a, isSaleRun));
      const bIndex = pinningSource.findIndex((id) => normalizeCompId(id) === selectionKeyForRow(b, isSaleRun));
      return aIndex - bIndex;
    });
    
    pinnedRowsData.top = orderedSelectedRows;

    // Add non-selected rows to regular rows
    regularRows.push(...processedRows.filter(row => !row.selected));

    return {
      rows: regularRows,
      pinnedRows: pinnedRowsData
    };
  }, [view, startingRows, isPinned, selectedComps, savedCompPids, reordering, reOrderingRef.current, unsavedChangesRef.current.unsavedRows, trainingMinIndexByParcelId, trainingUniquePids, isSaleRun]);

  // comparison function if the user has chosen comps other than the saved ones.
  const isSame = useMemo(() => {
    if (!savedCompPids || !selectedComps) return true;
    return (
      savedCompPids.length === selectedComps.length &&
      savedCompPids.every((pid, index) => pid === selectedComps[index])
    );
  }, [savedCompPids, selectedComps]);
  // If this value is true, your comps are different.
  const isDifferent = useMemo(() => !isSame, [isSame]); // true if different, false if the same
  const gridRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const theme = useTheme();

  const dataGridStyles = useMemo(() => createCaseReviewGridStyles(theme), [theme]);

  // Reset refs and internal grid states when user selects or re-orders a comp
  const cleaningFunction = useCallback(() => {
    handleApiOperations();
    if(reordering){setReordering(false)}
  }, [handleApiOperations, reordering]);

  // added rowupdate into dependencies. Not sure if necessary.
  // Removed all the stuff here related to thee old issaving logic.
  useEffect(() => { // refactor to a callback so you stop getting issues w/ apiref current being null on load.
    cleaningFunction();
  }, [comp, savedCompPids, view]);

  useEffect(() => {
    extraAddedRowsRef.current = [];
  }, [comp, view]);

  // Inventory update code:
  const handleProcessRowUpdate = useCallback((updatedRow, originalRow) => {
    // Use the stored original row if it exists, otherwise fallback to the actual current state
    const storedOriginalRow =
      unsavedChangesRef.current.rowsBeforeChange[updatedRow.id] ||
      (apiRef.current ? apiRef.current.getRow(updatedRow.id) : null) ||
      originalRow;

    const getChangedFields = (updated, original) => {
      return Object.keys(updated).reduce((acc, key) => {
        if (
          key !== 'PropertyInfo' &&
          key !== 'selected' &&
          key !== 'adj_price' &&
          key !== 'priceAdjustments' && // Exclude priceAdjustments object from unsaved changes
          key !== 'errors' && // Exclude errors array from unsaved changes
          key !== 'selectedRowNumber' && // Exclude UI state fields
          key !== 'isSelected' && // Exclude selection state
          typeof updated[key] !== 'object' && // Exclude any objects to prevent React rendering issues
          String(updated[key]) !== String(original[key]) // Use String conversion for type-safe comparison
        ) {
          acc[key] = updated[key];
        }
        return acc;
      }, {});
    };

    const changedFields = getChangedFields(updatedRow, storedOriginalRow);

    const existingChanges = unsavedChangesRef.current.unsavedRows[updatedRow.id] || {};

    if (Object.keys(changedFields).length > 0) {
      // If no original row is stored, store it now
      if (!unsavedChangesRef.current.rowsBeforeChange[updatedRow.id]) {
        unsavedChangesRef.current.rowsBeforeChange[updatedRow.id] = { ...storedOriginalRow };
      }

      // Include the pid field in the unsaved changes so it gets sent to the backend
      unsavedChangesRef.current.unsavedRows[updatedRow.id] = {
        ...existingChanges,
        ...changedFields,
        pid: updatedRow.parcel_id || updatedRow.pid, // Ensure pid is included for backend identification
      };
    } else {
      delete unsavedChangesRef.current.unsavedRows[updatedRow.id];
      delete unsavedChangesRef.current.rowsBeforeChange[updatedRow.id];
    }

    // Get all rows with unsaved changes and merge them with their current grid state
    const allUnsavedRows = Object.entries(unsavedChangesRef.current.unsavedRows).map(
      ([id, changes]) => {
        const existingRow = apiRef.current ? apiRef.current.getRow(id) : null;
        return {
          ...existingRow,
          ...changes,
        };
      }
    );

    if (apiRef.current) {
      apiRef.current.updateRows(allUnsavedRows);
    }
    forceToolbarUpdate();
    return updatedRow;
  }, [apiRef, forceToolbarUpdate]);
  
  
  const saveChanges = useCallback(async (additionalProps) => {
    // this function causes a MUITable rerender when the state updates, which causes the grid to rerender and not display the new user updated value.
    // Find some way to displayt he DB modal compoenent without causing a global rerender here
    try {
      // unsaved changes ref had no value.
      logger.log('starting saving')
      setConfirmModal(true);
    } catch (error) {
      logger.error('Error saving changes:', error);
    }
  }, []);

  // Pass this callback down to the subjtoolbar.
  const updateRefWithSubj = useCallback((newSubj, originalSubj) => {
    // Only compare fields present in newSubj
    const allMatch = Object.keys(newSubj).every(key => {
      // Map pid to parcel_id if needed
      if (key === 'pid' && originalSubj.parcel_id !== undefined) {
        return String(newSubj.pid) === String(originalSubj.parcel_id);
      }
      return String(newSubj[key]) === String(originalSubj[key]);
    });

    if (allMatch) {
      unsavedChangesRef.current.unsavedSubject = {};
      unsavedChangesRef.current.originalSubject = {};
      forceToolbarUpdate();
      return;
    }

    unsavedChangesRef.current.unsavedSubject = newSubj;
    unsavedChangesRef.current.originalSubject = originalSubj;
    forceToolbarUpdate();
  }, []);

  const selectedCompsRef = useRef(selectedComps);
  useEffect(() => {
    selectedCompsRef.current = selectedComps;
  }, [selectedComps]);

  // Will use this discard function later if the user doesn't commit their updates:
  const discardChanges = useCallback(() => {
    logger.log(unsavedChangesRef.current)
    const latestRows = Object.values(unsavedChangesRef.current.rowsBeforeChange).map((row) =>
      // apiRef.current.getRow(row.id) || row // Fallback to ref if grid is missing the row
    row
    );
    if (apiRef.current) {
      apiRef.current.updateRows(latestRows);
    }
    
    unsavedChangesRef.current = {
      originalSubject: {},
      unsavedRows: {},
      rowsBeforeChange: {},
      unsavedSubject: {},
    };
    forceToolbarUpdate();
    // use user selected comps if they exist:
    let savedCompOverride = savedCompPidsRef.current;
    if (selectedCompsRef.current?.length) savedCompOverride = selectedCompsRef.current;

    handleApiOperations(savedCompOverride);
  }, [apiRef, handleApiOperations]);

  // Update the handleCellClick function to use the new approach
  const handleCellClick = useCallback((params) => {
    if (params.colDef.headerClassName === 'actions') {
      // Attach apiRef to the column definition if not already present
      if (!params.colDef.apiRef && apiRef.current) {
        params.colDef.apiRef = apiRef;
        apiRef.current.updateColumns([params.colDef]);
      }

      if (reordering) {
        // Handle reordering logic
        const currentReordering = reOrderingRef.current || [];
        const rowKey = selectionKeyForRow(params.row, isSaleRun);
        const addedId = isSaleRun && params.row.TransactionID != null && params.row.TransactionID !== ''
          ? params.row.TransactionID
          : params.row.parcel_id;
        
        const updatedSelectedRowIds = currentReordering.includes(rowKey)
          || currentReordering.includes(params.row.parcel_id)
          ? currentReordering.filter(rowId => normalizeCompId(rowId) !== rowKey
            && rowId !== params.row.parcel_id)
          : [...currentReordering, addedId];
        
        // Update the reordering ref immediately
        reOrderingRef.current = updatedSelectedRowIds;
        
        // Create patches for all rows to update their selection state and numbers
        const patches = startingRows.map(row => {
          const rowKey = selectionKeyForRow(row, isSaleRun);
          const isSelected = updatedSelectedRowIds.some((id) => normalizeCompId(id) === rowKey);
          const selectedIndex = updatedSelectedRowIds.findIndex((id) => normalizeCompId(id) === rowKey);
          const selectedRowNumber = isSelected ? selectedIndex + 1 : 0;
          const wasPinned = selectionSetHasRow(selectedComps ?? savedCompPids ?? [], row, isSaleRun);
          
          return {
            id: row.id || compRowId(row.parcel_id, row.TransactionID),
            selected: isSelected || wasPinned,
            selectedRowNumber: selectedRowNumber
          };
        });

        // Update all rows in the grid immediately
        if (apiRef.current) {
          apiRef.current.updateRows(patches);
          
          // Set indeterminate state based on selection
          const actionsColumn = apiRef.current.getAllColumns().find(col => col.field === 'actions');
          if (actionsColumn) {
            actionsColumn.indeterminate = updatedSelectedRowIds.length > 0;
            apiRef.current.updateColumns([actionsColumn]);
          }
        }
        
        // Force a re-render to ensure visual updates
        setUserReordered(true);
      } else {
        // Normal selection handling — sales use TransactionID, assessment uses parcel_id
        const selectionKey = isSaleRun && params.row.TransactionID != null && params.row.TransactionID !== ''
          ? params.row.TransactionID
          : params.row.parcel_id;
        const wasSelected = params.row.selected;
        
        // Get current selection state
        const currentSelection = selectedComps ?? savedCompPids ?? [];
        const newSelection = wasSelected 
          ? currentSelection.filter(id => normalizeCompId(id) !== normalizeCompId(selectionKey)
            && id !== params.row.parcel_id)
          : [...currentSelection, selectionKey];

        // Create patches for the grid update
        const patches = [];

        // Update all rows to ensure correct numbering
        startingRows.forEach(row => {
          const rowKey = selectionKeyForRow(row, isSaleRun);
          const isSelected = newSelection.some((id) => normalizeCompId(id) === rowKey);
          const selectedIndex = newSelection.findIndex((id) => normalizeCompId(id) === rowKey);
          patches.push({
            id: row.id || compRowId(row.parcel_id, row.TransactionID),
            selected: isSelected,
            selectedRowNumber: isSelected ? selectedIndex + 1 : 0
          });
        });

        // Update the grid
        if (apiRef.current) {
          apiRef.current.updateRows(patches);
          
          // Get the actions column and update its state
          const columns = apiRef.current.getAllColumns();
          const actionsColumn = columns.find(col => col.field === 'actions');
          
          if (actionsColumn) {
            actionsColumn.indeterminate = newSelection.length > 0;
            actionsColumn.selectionArray = newSelection;
            apiRef.current.updateColumns([actionsColumn]);
          }
        }
        
        // Update the selection state
        updateSelectedComps(newSelection);
      }
    }
  }, [apiRef, reordering, selectedComps, savedCompPids, startingRows, updateSelectedComps, isSaleRun]);

  // Header "select all" calls handleSelectAllChange with colDef; it needs colDef.apiRef.
  // That was only set lazily on actions-column cellClick, so clicking the header first crashed.
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 90;
    const tryAttach = () => {
      if (cancelled || attempts++ > maxAttempts) return;
      const api = apiRef.current;
      if (!api) {
        requestAnimationFrame(tryAttach);
        return;
      }
      const actionsColumn = api.getAllColumns().find((col) => col.field === 'actions');
      if (!actionsColumn) {
        requestAnimationFrame(tryAttach);
        return;
      }
      if (!actionsColumn.apiRef) {
        actionsColumn.apiRef = apiRef;
        api.updateColumns([actionsColumn]);
      }
    };
    tryAttach();
    return () => {
      cancelled = true;
    };
  }, [apiRef, headers]);

  // Pin/unpin rebuilds row models (main vs pinned); the grid can re-apply columns from props and drop
  // custom colDef fields (selectionArray) used by the header checkbox. Re-sync from React state.
  useEffect(() => {
    if (!apiRef.current) return;
    const actionsColumn = apiRef.current.getAllColumns().find((col) => col.field === 'actions');
    if (!actionsColumn) return;
    const raw = selectedComps ?? savedCompPids;
    const selection = Array.isArray(raw) ? raw : [];
    actionsColumn.selectionArray = selection;
    actionsColumn.indeterminate = selection.length > 0;
    if (!actionsColumn.apiRef) actionsColumn.apiRef = apiRef;
    apiRef.current.updateColumns([actionsColumn]);
  }, [apiRef, selectedComps, savedCompPids, isPinned]);
  
    React.useEffect(() => {
      if (!apiRef.current) return;
      
      const cellClickSubscription = apiRef.current.subscribeEvent('cellClick', handleCellClick);
    
      // Cleanup the event listener on unmount or when dependencies change
      return () => {
        cellClickSubscription(); // Unsubscribe from the event
      };
    }, [apiRef, handleCellClick, isPinned]);

    // Update the addCompCallback function to add "returnWindow" error when a newly added comp has no other errors.
    const addCompCallback = useCallback(
      async ({ suggestion, isGooglePlace, place_id, parcel_id }) => {
        try {
          const propertyInfo = {
            parcel_id: parcel_id,
            address: suggestion,
          };

          const selectedRowIds = selectedComps ?? savedCompPids ?? [];
          if (!isSaleRun && selectedRowIds.some((id) => normalizeCompId(id) === normalizeCompId(parcel_id))) {
            toast.error('Comp already selected');
            return;
          }

          const newLoadingRow = {
            parcel_id: parcel_id,
            address: suggestion,
            id: parcel_id,
            isSelected: true,
            selectedRowNumber: selectedRowIds.length + 1,
            placeholder: true,
            PropertyInfo: propertyInfo
          };

          if (apiRef.current) {
            apiRef.current.updateRows([newLoadingRow]);
          }

          const newRow = await addCompNotInResponseCallback(parcel_id, comp);
          const removeLoadingRow = () => {
            if (apiRef.current) {
              apiRef.current.updateRows([{ id: parcel_id, _action: 'delete' }]);
            }
          };

          if (!newRow) {
            removeLoadingRow();
            return;
          }

          const existingRow = newRow.exists ? findCompInRawData(parcel_id, newRow.TransactionID) : null;
          const rowForSelection = existingRow || newRow;
          const selectionKey = selectionKeyForRow(rowForSelection, isSaleRun) || parcel_id;
          const rowId = existingRow?.id || compRowId(rowForSelection.parcel_id || parcel_id, rowForSelection.TransactionID);

          if (selectedRowIds.some((id) => normalizeCompId(id) === normalizeCompId(selectionKey))) {
            removeLoadingRow();
            toast.error('Comp already selected');
            return;
          }

          if (newRow.exists) {
            removeLoadingRow();
            if (!existingRow) {
              toast.error('Comp exists in data but not found in current table view');
              return;
            }

            const selectedIndex = selectedRowIds.length;
            if (apiRef.current) {
              apiRef.current.updateRows([isFiltered
                ? { ...existingRow, selected: true, selectedRowNumber: selectedIndex + 1, '#': 1 }
                : { id: rowId, selected: true, selectedRowNumber: selectedIndex + 1 }
              ]);
            }

            const updatedSelectedComps = [...selectedRowIds, selectionKey];
            if (apiRef.current) {
              apiRef.current.getAllColumns()[0].selectionArray = updatedSelectedComps;
              apiRef.current.getAllColumns()[0].indeterminate = true;
            }

            updateSelectedComps(updatedSelectedComps);
            toast.success(`Comp found and selected as #${updatedSelectedComps.length}. Save comps to keep.`);
            return;
          }

          const errors = createErrorsArray(newRow);
          const addedRow = {
            ...newRow,
            id: rowId,
            selected: true,
            selectedRowNumber: selectedRowIds.length + 1,
            errors: errors.length > 0 ? errors : undefined
          };
          extraAddedRowsRef.current = extraAddedRowsRef.current.filter((row) => row.id !== rowId);
          extraAddedRowsRef.current.push(addedRow);
          if (apiRef.current) {
            if (rowId !== parcel_id) {
              apiRef.current.updateRows([{ id: parcel_id, _action: 'delete' }]);
            }
            apiRef.current.updateRows([addedRow]);
          }

          const updatedSelectedComps = [...selectedRowIds, selectionKey];
          if (apiRef.current) {
            apiRef.current.getAllColumns()[0].selectionArray = updatedSelectedComps;
            apiRef.current.getAllColumns()[0].indeterminate = true;
          }

          updateSelectedComps(updatedSelectedComps);
          toast.success(
            `Comp added to selected comps as #${updatedSelectedComps.length}. Save comps to keep.`
          );

        } catch (error) {
          logger.error('Error searching for property:', error);
          toast.error('Error adding comp: ' + (error.response?.data?.message || error.message));
        }
      },
      [apiRef, selectedComps, savedCompPids, updateSelectedComps, comp, isSaleRun, findCompInRawData, isFiltered, addCompNotInResponseCallback]
    );


    // Update getRowClassName to use error states and handle weakPositive
    const getRowClassName = useCallback((params) => {
      // Add safety check
      if (!params.row) return '';
      let classNames = [];
      // Handle the four possible status values
      let statusClass;
      if (params.row.status === 'positive') {
        statusClass = 'positive';
      } else if (params.row.status === 'weakPositive') {
        statusClass = 'weak-positive';
      } else if (params.row.status === 'weakNegative') {
        statusClass = 'weak-negative';
      } else {
        statusClass = 'negative';
      }
      
      let isSelected = params.row.selected;

      if (selectedComps?.length) {
        isSelected = selectionSetHasRow(selectedComps, params.row, isSaleRun);
      } else if (selectedComps && selectedComps.length === 0) {
        isSelected = false;
      }
      
      if (params.row.errors && params.row.errors.length > 0) {
        classNames.push('error-border');
      }
      
      classNames.push(isSelected ? 'selected-comp' : `case-review--${statusClass}`);

      // Add training comp classes if this parcel is saved by training users
      const pid = params.row.parcel_id;
      const trainingUsers = trainingCompUsersByParcelId[pid];
      // If user selection equals training comps, suppress training highlight
      const selectionEqualsTraining = isSelectionEqualToTrainingUtil((selectedComps ?? savedCompPids ?? []), trainingUniquePids);

      if (!selectionEqualsTraining && trainingUsers && trainingUsers.length > 0) {
        classNames.push('training-comp');
        // Pick stable shade based on smallest userId for determinism (1..5)
        const minUserId = trainingUsers.reduce((a, b) => (a < b ? a : b));
        const shadeIdx = (minUserId % 5) + 1;
        classNames.push(`training-shade-${shadeIdx}`);
        // Assign per-user color by ordinal. If multiple users saved same comp, prefer lowest ordinal for determinism
        const ordinals = trainingUsers
          .map((uid) => trainingUserOrdinalMap[uid])
          .filter((n) => !!n)
          .sort((a, b) => a - b);
        const ordinal = ordinals.length > 0 ? ordinals[0] : 1;
        classNames.push(`training-user-ordinal-${ordinal}`);
      }
      
      return classNames.join(' ');
    }, [selectedComps, savedCompPids, trainingCompUsersByParcelId, trainingUserOrdinalMap, trainingUniquePids, isSaleRun]);

  // yellow coloring in grid for edited cells.
  const getCellClassName = useCallback((params) => {
    const rowId = params.row.id;
    const field = params.field;
    if(params.field !== 'pid'){
      const isCellEdited = !!unsavedChangesRef.current.unsavedRows[rowId]?.[field];
      return isCellEdited ? 'edited' : '';
    }
  }, []);

  const mergedProps = useMemo(() => ({
    rows: rows,
    columns: headers,
    ...tableProps,
    density: 'compact',
    disableVirtualization: false,
    autoHeight: false,
    getCacheFormatting: () => true,
    hideFooter: false,
    hideFooterPagination: false,
  }), [headers, tableProps, rows]);

  

  // User did not confirm to update database, close the modal and clear out the unsaved changes.
  const handleConfirmationClose = useCallback((responseObject=null, confirmed=false) => {
    // Always clear modal state first to prevent multiple modals
    setConfirmModal(false);
    setIsSaving(false);

    if(confirmed) {
      // Get current rows and their states
      if (apiRef.current) {
        const currentRows = apiRef.current.getRowModels();
        const updatedRows = Object.entries(currentRows).map(([_, row]) => {
          // Check if this row has any unsaved changes
          const unsavedChanges = unsavedChangesRef.current.unsavedRows[row.id];
          if (unsavedChanges) {
            // Apply the unsaved changes to the row
            return {
              ...row,
              ...unsavedChanges,
              // Preserve any other necessary state
              selected: row.selected,
              isSelected: row.isSelected,
              rowIndexPosition: row.rowIndexPosition
            };
          }
          return row;
        });

        // Update the grid with the modified rows
        apiRef.current.setRows(updatedRows);
      }
      
      // Don't clear the unsaved changes yet - they should persist until the DB update is complete
      // The cleanup will happen in handleProcessDBUpdate
    } else {
      // If not confirmed, revert changes
      logger.log('canceling modal');
      if(responseObject) {
        logger.log('response object');
      }
      discardChanges();
    }
  }, [apiRef, discardChanges, setIsSaving]);

  // only functions as local error handling would need to confirm the internal grid state is still correct. Haven't been able to get it to error to test.
  const handleProcessRowUpdateError = React.useCallback((error) => {
    logger.log('Error updating row:', error);
    unsavedChangesRef.current = {
      originalSubject: {},
      unsavedRows: {},
      rowsBeforeChange: {},
      unsavedSubject: {},
    };
  }, []);


  // This is incorrectly setting the saving state with a missing dependency before.
  const setIsSavingCallback = useCallback((isSaving) => {
    logger.log('running issaving callback')
    setIsSaving(true);
  }, []);

  const handleToggleReordering = useCallback(() => {
    if (reordering) {
      const confirmedIds = reOrderingRef.current || [];
      // Exiting reordering mode
      if (confirmedIds.length > 0) {
        updateSelectedComps(confirmedIds);
        handleApiOperations(confirmedIds);
        setUserReordered(true);
      }
    } else {
      // Starting reordering mode - ensure pins are enabled
      if (!isPinned) {
        setIsPinned(true);
        localStorage.setItem('pinnedComps', JSON.stringify(true));
      }
      // Initialize reordering ref with empty array to clear numbers
      reOrderingRef.current = [];
      
      // Force a re-render to clear numbers
      setUserReordered(true);
    }
    setReordering(!reordering);
  }, [reordering, isPinned, handleApiOperations, updateSelectedComps]);

  // intermediary callback to inject casenotes into the saveComps function.
  const handleSaveCompsWithSync = useCallback((idsToSave, saveOpts) => {
    logger.log('handleSaveCompsWithSync called');
    logger.log('Saving with values:', { idsToSave, saveOpts });
    handleSaveComps(idsToSave, saveOpts);
    setCaseNotesChanged(false);
  }, [handleSaveComps]);
// logger.log('muitable rerender')
  const handleCompChange = useCallback(() => {
    handleUpdateStateCallback();
    setCaseNotesChanged(false);
  }, [handleUpdateStateCallback]);

  const handleCaseNotesOrOfferChange = useCallback((changed) => {
    setCaseNotesChanged(changed);
  }, []);

  // Build negotiation comps data for toolbar when in negotiation view
  const negotiationCompsValues = useMemo(() => {
    if (view !== 'town' || !originalRows) return null;
    try {
      const negotiationComps = originalRows
        .filter((row) => {
          // Exclude subject and blank rows
          const isSubject = row?.isSubject;
          const hasAddress = !!(row?.Address) || !!(row?.PropertyInfo?.Address);
          return !isSubject && hasAddress;
        });
      
      return { comps: negotiationComps };
    } catch (error) {
      logger.error('Error filtering negotiation comps:', error);
      return null;
    }
  }, [view, originalRows]);

  return (
    <div className='w-full h-full flex flex-col min-h-0' ref={gridRef}>
          <div className='flex-shrink-0'>
            <TableCustomToolbar
              subject={subject}
              subjectIFMV={subjectIFMV}
              view={view}
              regularComps={regularComps}
              comps={selectedComps}
              memoizedSelectedComps={selectedComps}
              savedComps={savedCompPids}
              negotiationCompsValues={negotiationCompsValues}
              caseNotes={caseNotes}
              reviewDetails={reviewDetails}
              handleProcessDBUpdate={handleProcessDBUpdate}
              handleUpdateStateCallback={handleCompChange}
              viewCallback={viewCallback}
              changeCaseNotes={handleCaseNotesCleanup}
              onSaveComps={handleSaveCompsWithSync}
              onCompChange={handleCompChange}
              onCaseNotesChanged={handleCaseNotesOrOfferChange}
              caseNotesRef={caseNotesRef}
              onCaseInfoClick={onCaseInfoClick}
              caseObject={caseObject}
              caseId={caseId}
              isSettled={isSettled}
              townCompParcelIds={townCompParcelIds}
            />
          </div>

      {confirmModal && (
        <DBConfirmModal
          confirmModal={confirmModal}
          onDBUpdateSuccess={handleProcessDBUpdate}
          handleConfirmCancel={handleConfirmationClose}
          inventoryUpdates={unsavedChangesRef.current}
        />
      )}
      <div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
      {view === 'town' ? (
        <TownEvidenceTable
          rows={rows}
          townCompParcelIds={townCompParcelIds}
          subject={subject}
          updateNegotiationComps={updateNegotiationComps}
          handleSetPropertyInfo={handleSetPropertyInfo}
          unsavedChangesRef={unsavedChangesRef}
          handleDeleteProperty={handleDeleteProperty}
          negotiationCompsTouched={negotiationCompsTouched}
          markNegotiationCompsTouched={markNegotiationCompsTouched}
          headers={headers}
          tableProps={tableProps}
          handleAddressClick={handleAddressClick}
          viewCallback={viewCallback}
          addNegotiationComp={addNegotiationComp}
          memoizedSelectedAdjustments={memoizedSelectedAdjustments}
          savedComps={savedComps}
          memoizedCompObject={memoizedCompObject}
          caseObject={caseObject}
          dataGridStyles={dataGridStyles}
          updateRefWithSubj={updateRefWithSubj}
          isSettled={isSettled}
          setIsPinned={handlePinToggle}
          isPinned={isPinned}
          setIsSavingCallback={setIsSavingCallback}
          handleSaveComps={handleSaveCompsWithSync}
          saveChanges={saveChanges}
          discardChanges={discardChanges}
          handleToggleReordering={handleToggleReordering}
          reordering={reordering}
          reOrderingRef={reOrderingRef}
          userReordered={userReordered}
          compNumber={comp}
          isSaving={isSaving}
        />
      ) : (
        // Render regular DataGridContainer for other views
        <DataGridContainer
          key={`regular-datagrid-${view}`}
          rowUpdate={rowUpdate}
          view={view}
          addCompCallback={addCompCallback}
          setIsPinned={handlePinToggle}
          handleSetPropertyInfo={handleSetPropertyInfo}
          caseNotesChanged={caseNotesChanged}
          discardChanges={discardChanges}
          comps={originalRows}
          subject={subject}
          setIsSavingCallback={setIsSavingCallback}
          handleProcessRowUpdate={handleProcessRowUpdate}
          mergedProps={mergedProps}
          dataGridStyles={dataGridStyles}
          pinnedRows={pinnedRows}
          userUpdate={selectedComps}
          selectedRows={selectedComps}
          apiRef={apiRef}
          memoizedColumnVisibilityModel={memoizedColumnVisibilityModel}
          handleColumnVisibilityChange={handleColumnVisibilityChange}
          columnLayout={columnLayout}
          getRowClassName={getRowClassName}
          getCellClassName={getCellClassName}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          headers={headers}
          filteredHeaders={filteredHeaders}
          isDifferent={
            isDifferent ||
            (
              getCaseNotes[comp] != null &&
              caseObject?.CaseNotes != null &&
              getCaseNotes[comp] !== caseObject.CaseNotes
            )
          }
          saveChanges={saveChanges}
          unsavedChangesRef={unsavedChangesRef}
          updateRefWithSubj={updateRefWithSubj}
          handleSaveComps={handleSaveCompsWithSync}
          handleToggleReordering={handleToggleReordering}
          isSettled={isSettled}
          reordering={reordering}
          reOrderingRef={reOrderingRef}
          userReordered={userReordered}
          isSaving={isSaving}
          savedCompPids={savedCompPids}
          isPinned={isPinned}
          onFilteredRowsChange={onFilteredRowsChange}
          mapFilteredParcelIds={mapFilteredParcelIds}
          onClearMapFilter={onClearMapFilter}
          onManualRefresh={onManualRefresh}
          showRefreshButton={showRefreshButton}
          isRefreshing={isRefreshing}
          hideToolbar={hideToolbar}
          isSaleRun={isSaleRun}
        />
      )}
      </div>
    </div>
  );
});

if (process.env.NODE_ENV === 'development') {
  MuiTable.whyDidYouRender = true;
}

export default MuiTable;